import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp } from 'lucide-react';

interface CompanyLogoProps {
  src: string | undefined;
  alt: string;
  className?: string;
  iconClassName?: string;
  fallbackIconSize?: number;
}

const CompanyLogo: React.FC<CompanyLogoProps> = ({
  src,
  alt,
  className = "w-10 h-10 p-1.5 bg-background-light dark:bg-background-light object-contain rounded-full",
  iconClassName = "p-0.5 text-primary-light dark:text-primary-dark rounded-full bg-background-light dark:bg-background-light",
  fallbackIconSize = 38
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = useRef(false); // Track if image has loaded successfully

  // Add logging for debugging
  useEffect(() => {
    // console.log('[CompanyLogo] Component mounted/updated:', {
    //   src,
    //   alt,
    //   imageError,
    //   isLoading,
    //   srcType: typeof src,
    //   srcLength: src?.length,
    //   srcTrimmed: src?.trim(),
    //   isEmpty: !src || src.trim() === '',
    //   hasLoaded: hasLoadedRef.current
    // });
  }, [src, alt, imageError, isLoading]);

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // console.log('[CompanyLogo] Image error occurred:', {
    //   src,
    //   alt,
    //   naturalWidth: event.currentTarget.naturalWidth,
    //   naturalHeight: event.currentTarget.naturalHeight,
    //   complete: event.currentTarget.complete
    // });
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setImageError(true);
    setIsLoading(false);
    hasLoadedRef.current = false;
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // console.log('[CompanyLogo] Image loaded successfully:', {
    //   src,
    //   alt,
    //   naturalWidth: event.currentTarget.naturalWidth,
    //   naturalHeight: event.currentTarget.naturalHeight,
    //   complete: event.currentTarget.complete
    // });
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsLoading(false);
    setImageError(false);
    hasLoadedRef.current = true; // Mark as loaded
  };

  // Add loading state start log and timeout
  useEffect(() => {
    if (src && src.trim() !== '') {
      // Only reset loading state if the image hasn't loaded yet or if src actually changed
      if (!hasLoadedRef.current) {
        // console.log('[CompanyLogo] Starting to load image:', src);
        setIsLoading(true);
        setImageError(false);
        
        // Set a timeout to handle stuck loading states
        timeoutRef.current = setTimeout(() => {
          // console.log('[CompanyLogo] Image loading timeout, showing fallback:', src);
          setImageError(true);
          setIsLoading(false);
          hasLoadedRef.current = false;
        }, 10000); // 10 second timeout
      } else {
        // console.log('[CompanyLogo] Image already loaded, skipping loading state reset');
      }
    } else {
      // console.log('[CompanyLogo] No valid src, showing fallback immediately');
      setIsLoading(false);
      setImageError(true);
      hasLoadedRef.current = false;
    }

    // Cleanup timeout on unmount or src change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [src]);

  // Log the decision logic
  const shouldShowFallback = imageError || !src || src.trim() === '';
  // console.log('[CompanyLogo] Render decision:', {
    // src,
    // alt,
    // shouldShowFallback,
    // imageError,
    // isLoading,
    // srcExists: !!src,
    // srcNotEmpty: src ? src.trim() !== '' : false
  // });

  // If image failed to load or is invalid, show TrendingUp icon
  if (shouldShowFallback) {
    // console.log('[CompanyLogo] Rendering fallback icon');
    return (
      <div className={`${className} flex items-center justify-center`}>
        <TrendingUp size={fallbackIconSize} className={iconClassName} />
      </div>
    );
  }

  // console.log('[CompanyLogo] Rendering image with loading state:', { isLoading });
  return (
    <div className="relative">
      {isLoading && (
        <div className={`${className} flex items-center justify-center`}>
        <TrendingUp size={fallbackIconSize} className={`${iconClassName} animate-pulse`} />
      </div>
      )}
      <img 
        key={src} // Force re-render when src changes
        src={src} 
        alt={alt} 
        className={className}
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{ display: isLoading ? 'none' : 'block' }}
      />
    </div>
  );
};

export default CompanyLogo; 